<template>
  <section class="aura-hero">
    <!-- Left: text content -->
    <div class="hero-container">
      <div class="hero-left" :class="{ 'is-visible': isVisible }">
        <!-- Top badge -->
        <div class="top-badge anim-item anim-delay-0">
          <svg class="badge-star" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 2l2.4 7.4H22l-6.2 4.5 2.4 7.4L12 17l-6.2 4.3 2.4-7.4L2 9.4h7.6z"/>
          </svg>
          <span>{{ $t('licenseLabel') }} 81643 &nbsp;·&nbsp; {{ $t('trustedLabel') }}</span>
        </div>

        <!-- Headline -->
        <h1 class="serif-headline anim-item anim-delay-1">
          {{ $t('heroHeadline1') }}<br />
          {{ $t('heroHeadline2') }}<br />
          <em class="headline-accent">{{ $t('heroHeadline3') }}</em>
        </h1>

        <!-- Sub -->
        <p class="hero-sub anim-item anim-delay-2">{{ $t('heroMsg') }}</p>

        <!-- CTAs -->
        <div class="cta-row anim-item anim-delay-3">
          <router-link to="/matching" class="btn-dark">
            {{ $t('MatchNow') }}
            <svg class="arrow-icon" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M4 10h12M11 5l5 5-5 5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </router-link>
          <a href="https://wa.me/85253425261" target="_blank" class="btn-wa">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
            </svg>
            WhatsApp: 5342 5261
          </a>
        </div>

        <!-- Trust indicators -->
        <div class="trust-row anim-item anim-delay-4">
          <div class="trust-item">
            <div class="trust-emoji">🏠</div>
            <div class="trust-text">
              <span class="trust-label">{{ $t('happyClients') }}</span>
              <span class="trust-sub">{{ $t('acrossHK') }}</span>
            </div>
          </div>

          <div class="trust-divider"></div>

          <div class="trust-item">
            <div class="shield-wrap">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M9 12l2 2 4-4" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </div>
            <div class="trust-text">
              <span class="trust-label">{{ $t('licensedInsured') }}</span>
              <span class="trust-sub">{{ $t('vettedProfessionals') }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Right: photo -->
      <div class="hero-right" :class="{ 'is-visible': isVisible }">
        <div class="image-frame">
          <img
            src="img/hero-ai.jpg"
            alt="AI-powered domestic helper matching"
            class="hero-photo"
          />
          <div class="photo-gradient"></div>

          <!-- Floating review card -->
          <div class="review-card">
            <div class="review-icon-wrap">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" xmlns="http://www.w3.org/2000/svg">
                <path d="M22 11.08V12a10 10 0 11-5.93-9.14" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                <polyline points="22 4 12 14.01 9 11.01" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </div>
            <transition name="review-fade" mode="out-in">
              <div class="review-body" :key="activeIndex">
                <p class="review-title">— {{ currentTestimonial.name }}</p>
                <p class="review-quote">"{{ truncated }}"</p>
                <div class="review-stars">
                  <svg v-for="i in 5" :key="i" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2l2.4 7.4H22l-6.2 4.5 2.4 7.4L12 17l-6.2 4.3 2.4-7.4L2 9.4h7.6z"/></svg>
                </div>
                <div class="review-dots">
                  <span
                    v-for="(_, i) in testimonials"
                    :key="i"
                    class="review-dot"
                    :class="{ active: i === activeIndex }"
                  ></span>
                </div>
              </div>
            </transition>
          </div>
        </div>

        <!-- Decorative blobs -->
        <div class="blob-amber"></div>
        <div class="blob-slate"></div>
      </div>
    </div>

    <!-- Services strip -->
    <div class="services-strip">
      <div class="services-inner">
        <div class="service-chip">
          <span class="ti-home"></span>{{ $t('Oversea') }}
        </div>
        <div class="service-chip">
          <span class="ti-id-badge"></span>{{ $t('Local') }}
        </div>
        <div class="service-chip">
          <span class="ti-reload"></span>{{ $t('Contract') }}
        </div>
        <div class="service-chip">
          <span class="ti-comment-alt"></span>WhatsApp {{ $t('matchConsult') }}
        </div>
      </div>
    </div>
  </section>
</template>

<script>
export default {
  name: "PremiumHero",
  props: {
    testimonials: {
      type: Array,
      default: () => [
        { content: "最好嘅地方系貴公司有比較多外傭给我去選擇, 並且清楚知道客人的要求而且十分有效地幫我去篩選合適的外傭而给我挑選, 解决我事前恐怕找不到好外傭的煩惱, 謝謝幫忙🙏", name: "P小姐" },
      ],
    },
  },
  data() {
    return {
      isVisible: false,
      activeIndex: 0,
      timer: null,
    };
  },
  computed: {
    currentTestimonial() {
      return this.testimonials[this.activeIndex] || this.testimonials[0];
    },
    truncated() {
      const text = this.currentTestimonial.content.replace(/\n/g, ' ').trim();
      return text.length > 90 ? text.slice(0, 90).trimEnd() + '…' : text;
    },
  },
  mounted() {
    requestAnimationFrame(() => {
      this.isVisible = true;
    });
    if (this.testimonials.length > 1) {
      this.timer = setInterval(() => {
        this.activeIndex = (this.activeIndex + 1) % this.testimonials.length;
      }, 5000);
    }
  },
  beforeDestroy() {
    clearInterval(this.timer);
  },
};
</script>

<style scoped>
/* ── Base ── */
.aura-hero {
  background-color: #FDFCFB;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  font-family: 'Montserrat', 'Open Sans', sans-serif;
  color: #085E5B;
  overflow: hidden;
  padding-top: 80px;
}

/* ── Two-column layout ── */
.hero-container {
  flex: 1;
  max-width: 1280px;
  width: 100%;
  margin: 0 auto;
  padding: 48px 48px 40px;
  display: flex;
  align-items: center;
  gap: 64px;
}

/* ── Left content ── */
.hero-left {
  flex: 1;
  max-width: 580px;
  padding-top: 16px;
}

/* ── Animation system ── */
.anim-item {
  opacity: 0;
  transform: translateY(24px);
  transition: opacity 0.7s cubic-bezier(0.22, 1, 0.36, 1), transform 0.7s cubic-bezier(0.22, 1, 0.36, 1);
}
.is-visible .anim-item { opacity: 1; transform: translateY(0); }
.anim-delay-0 { transition-delay: 0.1s; }
.anim-delay-1 { transition-delay: 0.25s; }
.anim-delay-2 { transition-delay: 0.4s; }
.anim-delay-3 { transition-delay: 0.55s; }
.anim-delay-4 { transition-delay: 0.7s; }

/* ── Top badge ── */
.top-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: #E4F6F5;
  border: 1px solid rgba(180, 120, 0, 0.2);
  border-radius: 50px;
  padding: 6px 16px;
  margin-bottom: 28px;
  font-size: 13px;
  font-weight: 600;
  color: #0A7A76;
  letter-spacing: 0.2px;
}
.badge-star {
  width: 14px;
  height: 14px;
  color: #F5A340;
  flex-shrink: 0;
}

/* ── Headline ── */
.serif-headline {
  font-family: 'Playfair Display', 'Georgia', 'Times New Roman', serif;
  font-size: clamp(32px, 4.5vw, 56px);
  font-weight: 500;
  line-height: 1.12;
  color: #0A7A76;
  margin: 0 0 24px;
  letter-spacing: -0.5px;
}
.headline-accent {
  color: #94a3b8;
  font-style: italic;
  font-weight: 400;
}

/* ── Sub ── */
.hero-sub {
  font-size: clamp(15px, 1.5vw, 17px);
  color: #475569;
  line-height: 1.75;
  max-width: 480px;
  font-weight: 300;
  margin-bottom: 40px;
}

/* ── CTA row ── */
.cta-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 20px;
  margin-bottom: 48px;
}
.btn-dark {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: #0A7A76;
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  padding: 13px 28px;
  border-radius: 50px;
  text-decoration: none;
  box-shadow: 0 2px 12px rgba(15, 23, 42, 0.18);
  transition: background 0.25s, transform 0.25s, box-shadow 0.25s;
  letter-spacing: 0.2px;
}
.btn-dark:hover {
  background: #085E5B;
  transform: translateY(-2px);
  box-shadow: 0 6px 24px rgba(15, 23, 42, 0.25);
  color: #fff;
  text-decoration: none;
}
.arrow-icon {
  width: 18px;
  height: 18px;
  transition: transform 0.25s;
}
.btn-dark:hover .arrow-icon {
  transform: translateX(4px);
}
.btn-wa {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 600;
  color: #0A7A76;
  text-decoration: none;
  transition: color 0.2s;
}
.btn-wa:hover { color: #64748b; text-decoration: none; }

/* ── Trust row ── */
.trust-row {
  display: flex;
  align-items: center;
  gap: 32px;
  padding-top: 32px;
  border-top: 1px solid #e2e8f0;
  flex-wrap: wrap;
}
.trust-item {
  display: flex;
  align-items: center;
  gap: 12px;
}
.trust-divider {
  width: 1px;
  height: 40px;
  background: #e2e8f0;
  flex-shrink: 0;
}
.trust-emoji {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #E4F6F5;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  flex-shrink: 0;
}
.trust-text {
  display: flex;
  flex-direction: column;
}
.trust-label {
  font-size: 13px;
  font-weight: 600;
  color: #0A7A76;
  line-height: 1.3;
}
.trust-sub {
  font-size: 12px;
  color: #64748b;
  line-height: 1.3;
}
.shield-wrap {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #E4F6F5;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.shield-wrap svg {
  width: 20px;
  height: 20px;
  color: #45C9C2;
}

/* ── Right image ── */
.hero-right {
  flex: 0 0 auto;
  width: 480px;
  position: relative;
  opacity: 0;
  transform: scale(0.96);
  filter: blur(8px);
  transition: opacity 1s cubic-bezier(0.22, 1, 0.36, 1) 0.2s,
              transform 1s cubic-bezier(0.22, 1, 0.36, 1) 0.2s,
              filter 1s cubic-bezier(0.22, 1, 0.36, 1) 0.2s;
}
.hero-right.is-visible {
  opacity: 1;
  transform: scale(1);
  filter: blur(0px);
}

.image-frame {
  position: relative;
  border-radius: 40px;
  overflow: hidden;
  aspect-ratio: 4/5;

  box-shadow: 0 32px 80px rgba(15, 23, 42, 0.12), 0 8px 24px rgba(15, 23, 42, 0.08);
}
.hero-photo {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: 75% center;
  display: block;
}
.photo-gradient {
  position: absolute;
  inset: 0;
  background: linear-gradient(to top, rgba(15,23,42,0.25) 0%, transparent 40%);
}

/* ── Floating review card ── */
.review-card {
  position: absolute;
  bottom: 28px;
  left: 24px;
  right: 24px;
  background: rgba(255,255,255,0.92);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  border-radius: 20px;
  padding: 18px 20px;
  box-shadow: 0 16px 48px rgba(0,0,0,0.14);
  display: flex;
  align-items: flex-start;
  gap: 14px;
  animation: cardFadeIn 0.8s cubic-bezier(0.22, 1, 0.36, 1) 1s both;
}
@keyframes cardFadeIn {
  from { opacity: 0; transform: translateY(16px); }
  to   { opacity: 1; transform: translateY(0); }
}
.review-icon-wrap {
  width: 38px;
  height: 38px;
  border-radius: 50%;
  background: #0A7A76;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.review-icon-wrap svg {
  width: 18px;
  height: 18px;
  color: #fff;
}
.review-body { flex: 1; }
.review-title {
  font-size: 13px;
  font-weight: 700;
  color: #0A7A76;
  margin: 0 0 4px;
  line-height: 1.3;
}
.review-quote {
  font-size: 11.5px;
  color: #64748b;
  line-height: 1.5;
  margin: 0 0 8px;
}
.review-stars {
  display: flex;
  gap: 2px;
}
.review-stars svg {
  width: 12px;
  height: 12px;
  color: #F5A340;
}

/* ── Decorative blobs ── */
.blob-amber {
  position: absolute;
  z-index: -1;
  right: -60px;
  top: 20px;
  width: 340px;
  height: 340px;
  border-radius: 50%;
  background: #E4F6F5;
  filter: blur(80px);
  opacity: 0.6;
  pointer-events: none;
  mix-blend-mode: multiply;
}
.blob-slate {
  position: absolute;
  z-index: -1;
  left: -60px;
  bottom: 40px;
  width: 280px;
  height: 280px;
  border-radius: 50%;
  background: #f1f5f9;
  filter: blur(80px);
  opacity: 0.9;
  pointer-events: none;
  mix-blend-mode: multiply;
}

/* ── Services strip ── */
.services-strip {
  border-top: 1px solid #f1f5f9;
  padding: 20px 48px 28px;
}
.services-inner {
  max-width: 1280px;
  margin: 0 auto;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}
.service-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 50px;
  padding: 7px 18px;
  color: #475569;
  font-size: 13px;
  font-weight: 500;
  transition: background 0.2s, border-color 0.2s;
}
.service-chip:hover {
  background: #f1f5f9;
  border-color: #cbd5e1;
}
.service-chip span { margin-right: 4px; }

/* ── Review fade transition ── */
.review-fade-enter-active,
.review-fade-leave-active {
  transition: opacity 0.4s ease, transform 0.4s ease;
}
.review-fade-enter {
  opacity: 0;
  transform: translateY(8px);
}
.review-fade-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

/* ── Progress dots ── */
.review-dots {
  display: flex;
  gap: 5px;
  margin-top: 8px;
}
.review-dot {
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: #cbd5e1;
  transition: background 0.3s, transform 0.3s;
}
.review-dot.active {
  background: #0A7A76;
  transform: scale(1.3);
}

/* ── Responsive ── */
@media (max-width: 1024px) {
  .hero-container { padding: 48px 32px 40px; gap: 40px; }
  .hero-right { width: 400px; }
}

@media (max-width: 768px) {
  .aura-hero { padding-top: 70px; }
  .hero-container {
    flex-direction: column;
    padding: 40px 24px 32px;
    gap: 40px;
    text-align: center;
  }
  .hero-left { max-width: 100%; }
  .top-badge { margin: 0 auto 24px; }
  .hero-sub { margin-left: auto; margin-right: auto; }
  .cta-row { justify-content: center; }
  .trust-row { justify-content: center; gap: 20px; }
  .trust-divider { display: none; }
  .hero-right { width: 100%; max-width: 420px; margin: 0 auto; }
  .services-strip { padding: 20px 24px 24px; }
}

@media (max-width: 480px) {
  .hero-container { padding: 32px 16px 24px; }
  .image-frame { border-radius: 28px; }
  .review-card { bottom: 16px; left: 14px; right: 14px; padding: 14px 16px; }
  .services-strip { padding: 16px 16px 20px; }
}
</style>
